import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AdminPageWrapper } from '@/components/admin/AdminPageWrapper';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { 
  Bell, 
  ArrowLeft, 
  Check, 
  CheckCheck,
  Trash2, 
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  ExternalLink,
  Filter,
  Send,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { NotificationBell } from '@/components/NotificationBell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de notifications avec labels et icônes
const NOTIFICATION_TYPE_LABELS = {
  order_new: { label: "Nouvelle commande", icon: CheckCircle, color: "text-green-500" },
  order_blocked: { label: "Commande bloquée", icon: AlertCircle, color: "text-red-500" },
  order_qc_ready: { label: "QC disponible", icon: Info, color: "text-blue-500" },
  stock_out: { label: "Rupture stock", icon: AlertTriangle, color: "text-yellow-500" },
  price_change: { label: "Changement prix", icon: AlertTriangle, color: "text-yellow-500" },
  source_dead: { label: "URL morte", icon: AlertCircle, color: "text-red-500" },
  agent_balance_low: { label: "Solde faible", icon: AlertTriangle, color: "text-yellow-500" },
  agent_balance_critical: { label: "Solde critique", icon: AlertCircle, color: "text-red-500" },
  playwright_failed: { label: "Playwright échoué", icon: AlertCircle, color: "text-red-500" },
  review_negative: { label: "Avis négatif", icon: AlertTriangle, color: "text-yellow-500" },
  crew_join: { label: "Demande Crew", icon: Info, color: "text-blue-500" },
  crew_level_up: { label: "Montée niveau Crew", icon: CheckCircle, color: "text-green-500" },
  system: { label: "Système", icon: Info, color: "text-white" },
  test: { label: "Test", icon: Info, color: "text-purple-500" },
};

export function AdminNotificationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({});
  const [typeStats, setTypeStats] = useState({});
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Filtres
  const [filterType, setFilterType] = useState(searchParams.get('type') || 'all');
  const [filterRead, setFilterRead] = useState(searchParams.get('read') || 'all');
  const [filterSeverity, setFilterSeverity] = useState(searchParams.get('severity') || 'all');

  const getToken = () => localStorage.getItem('admin_token');

  // Charger les notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        navigate('/admin');
        return;
      }

      const params = new URLSearchParams({ page, limit: 20 });
      if (filterType !== 'all') params.append('type', filterType);
      if (filterRead !== 'all') params.append('read', filterRead === 'read' ? 'true' : 'false');
      if (filterSeverity !== 'all') params.append('severity', filterSeverity);

      const response = await fetch(`${API_URL}/api/admin/notifications?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('admin_token');
          navigate('/admin');
          return;
        }
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();
      setNotifications(data.notifications || []);
      setTotal(data.total || 0);
      setUnreadCount(data.unread_count || 0);
      setTotalPages(data.pages || 1);
      setTypeStats(data.type_stats || {});
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  }, [page, filterType, filterRead, filterSeverity, navigate]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Mettre à jour les params URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (filterType !== 'all') params.set('type', filterType);
    if (filterRead !== 'all') params.set('read', filterRead);
    if (filterSeverity !== 'all') params.set('severity', filterSeverity);
    setSearchParams(params);
  }, [filterType, filterRead, filterSeverity, setSearchParams]);

  // Marquer comme lu
  const markAsRead = async (ids = null, all = false) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/notifications/mark-read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(all ? { all: true } : { ids }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.marked} notification(s) marquée(s) comme lue(s)`);
        fetchNotifications();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Supprimer les lues
  const clearRead = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/notifications/clear-read`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.deleted} notification(s) supprimée(s)`);
        fetchNotifications();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Envoyer notification de test
  const sendTestNotification = async (severity = 'info') => {
    try {
      setSending(true);
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/notifications/test?severity=${severity}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Notification de test envoyée !');
        fetchNotifications();
      } else {
        throw new Error('Failed to send test');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du test');
    } finally {
      setSending(false);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      error: "bg-red-500/20 text-red-400 border-red-500/30",
      warning: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      success: "bg-green-500/20 text-green-400 border-green-500/30",
      info: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    };
    return styles[severity] || styles.info;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short'
    });
  };

  const getTypeLabel = (type) => {
    return NOTIFICATION_TYPE_LABELS[type]?.label || type;
  };

  return (
    <AdminPageWrapper title="Centre de Notifications" subtitle={`${total} notification(s) • ${unreadCount} non lue(s)`}>
      <div className="space-y-6" data-testid="admin-notifications-page">
        {/* Actions rapides */}
        <div className="flex flex-wrap gap-4 mb-6">
          <Card className="bg-zinc-900/50 border-zinc-800 flex-1 min-w-[200px]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm">Non lues</p>
                  <p className="text-2xl font-bold text-white">{unreadCount}</p>
                </div>
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                  <Bell className="w-5 h-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800 flex-1 min-w-[200px]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm">Erreurs</p>
                  <p className="text-2xl font-bold text-red-400">
                    {typeStats.order_blocked || 0 + typeStats.playwright_failed || 0 + typeStats.source_dead || 0 + typeStats.agent_balance_critical || 0}
                  </p>
                </div>
                <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800 flex-1 min-w-[200px]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm">Commandes</p>
                  <p className="text-2xl font-bold text-green-400">{typeStats.order_new || 0}</p>
                </div>
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtres et actions */}
        <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Filtres */}
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-white" />
                <span className="text-sm text-white">Filtres:</span>
              </div>

              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-700" data-testid="filter-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {Object.entries(NOTIFICATION_TYPE_LABELS).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterRead} onValueChange={setFilterRead}>
                <SelectTrigger className="w-[150px] bg-zinc-800 border-zinc-700" data-testid="filter-read">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="unread">Non lues</SelectItem>
                  <SelectItem value="read">Lues</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                <SelectTrigger className="w-[150px] bg-zinc-800 border-zinc-700" data-testid="filter-severity">
                  <SelectValue placeholder="Sévérité" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="error">Erreur</SelectItem>
                  <SelectItem value="warning">Avertissement</SelectItem>
                  <SelectItem value="success">Succès</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex-1" />

              {/* Actions */}
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchNotifications}
                className="border-zinc-700"
                data-testid="refresh-notifications"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>

              <Button 
                variant="outline" 
                size="sm"
                onClick={() => markAsRead(null, true)}
                disabled={unreadCount === 0}
                className="border-zinc-700"
                data-testid="mark-all-read"
              >
                <CheckCheck className="w-4 h-4 mr-2" />
                Tout marquer lu
              </Button>

              <Button 
                variant="outline" 
                size="sm"
                onClick={clearRead}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                data-testid="clear-read"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Effacer lues
              </Button>

              <Button 
                size="sm"
                onClick={() => sendTestNotification('info')}
                disabled={sending}
                className="bg-primary hover:bg-primary/90"
                data-testid="send-test-notification"
              >
                <Send className={`w-4 h-4 mr-2 ${sending ? 'animate-pulse' : ''}`} />
                Envoyer notification de test
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Liste des notifications */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Notifications</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-12 text-center">
                <RefreshCw className="w-8 h-8 text-gray-200 mx-auto animate-spin" />
                <p className="text-white mt-2">Chargement...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-white">Aucune notification</p>
                <p className="text-sm text-gray-200 mt-1">
                  Les notifications apparaîtront ici
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`p-4 rounded-lg border transition-colors ${
                      !notif.read 
                        ? 'bg-zinc-800/50 border-zinc-700' 
                        : 'bg-zinc-900/30 border-zinc-800'
                    }`}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icône sévérité */}
                      <div className="mt-0.5">
                        {getSeverityIcon(notif.severity)}
                      </div>

                      {/* Contenu */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={getSeverityBadge(notif.severity)}>
                            {notif.severity}
                          </Badge>
                          <Badge variant="outline" className="text-white border-zinc-700">
                            {getTypeLabel(notif.type)}
                          </Badge>
                          {!notif.read && (
                            <span className="w-2 h-2 bg-primary rounded-full" />
                          )}
                        </div>
                        
                        <p className={`text-sm ${!notif.read ? 'text-white font-medium' : 'text-white'}`}>
                          {notif.message}
                        </p>
                        
                        <p className="text-xs text-gray-200 mt-1">
                          {formatTime(notif.created_at)}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {notif.link && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(notif.link)}
                            className="text-white hover:text-white"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        )}
                        {!notif.read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => markAsRead([notif.id])}
                            className="text-white hover:text-white"
                            data-testid={`mark-read-${notif.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-zinc-800">
                <p className="text-sm text-white">
                  Page {page} sur {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="border-zinc-700"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="border-zinc-700"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info push notifications */}
        <Card className="bg-zinc-900/50 border-zinc-800 mt-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Bell className="w-5 h-5 text-blue-500" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">Notifications Push Navigateur</p>
                <p className="text-sm text-white">
                  Les alertes de sévérité "error" déclenchent automatiquement une notification push si vous avez accordé la permission.
                </p>
              </div>
              <Badge className={
                typeof Notification !== 'undefined' && Notification.permission === 'granted' 
                  ? "bg-green-500/20 text-green-400" 
                  : "bg-zinc-700 text-white"
              }>
                {typeof Notification !== 'undefined' 
                  ? Notification.permission === 'granted' 
                    ? 'Activées' 
                    : Notification.permission === 'denied'
                      ? 'Bloquées'
                      : 'Non autorisées'
                  : 'Non supportées'
                }
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminPageWrapper>
  );
}

export default AdminNotificationsPage;
